import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { ListProps, SelectType } from '../protocols';
import { Option } from '../styles';
import { Content } from './styles';

export const Lista: React.FC<ListProps> = ({
  inputRef,
  setIsOpen,
  setFilter,
  hasNextPage,
  fetchNextPage,
  data,
  selected,
  setSelected,
  isFetching,
  changeSelected,
  queryName,
  queryClient,
  listaWidth,
  allowNone = true,
  noneOptionLabel = 'Nenhuma',
}) => {
  const wrapperRef = useRef<HTMLUListElement | null>(null);

  const useCloseOptions = (ref: any) => {
    useEffect(() => {
      const handleClickOutside = (event: any) => {
        if (ref.current && !ref.current.contains(event.target)) {
          if (inputRef) inputRef.current.value = '';
          setFilter('');
          queryClient.removeQueries(queryName);
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
  };

  useCloseOptions(wrapperRef);

  const onScroll = async () => {
    if (!wrapperRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = wrapperRef.current;
    const zoomLevel = Math.round(window.devicePixelRatio * 100);
    const offset = Math.abs(100 - zoomLevel);

    if (scrollTop + clientHeight >= scrollHeight - offset) {
      if (hasNextPage) await fetchNextPage();
    }
  };

  const handleClick = useCallback(
    (option: SelectType) => {
      option.label = `${option.label}`;
      setSelected(option);
      changeSelected(option);
      setIsOpen(false);
    },
    [changeSelected, setIsOpen, setSelected],
  );

  const flatItems: SelectType[] = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((p: any) => p?.data || []);
  }, [data]);

  const uniqueLojas = useMemo(() => {
    const set = new Set<number>();
    flatItems.forEach((i: any) => {
      if (typeof i.lojaId === 'number') set.add(i.lojaId);
    });
    return Array.from(set.values()).sort((a, b) => a - b);
  }, [flatItems]);

  const hasMultipleLojas = uniqueLojas.length > 1;

  const applySelectionUX = (item: any) => {
    if (!inputRef) return;

    const isNone = item?.value === -1;
    const placeholderText = isNone
      ? `${item.label}`
      : `${item.value} - ${item.label}`;

    inputRef.current.placeholder = placeholderText;
    inputRef.current.value = '';

    queryClient.removeQueries(queryName);
    setFilter('');
  };

  const renderItems = (items: any[]) =>
    items.map((item) => {
      const isNone = item?.value === -1;
      const isSelected = selected?.value === item.value;

      return (
        <Option
          key={`${item.lojaId ?? 's'}-${item.value}`}
          className={isSelected ? 'selected' : ''}
          onClick={() => {
            handleClick(item);
            applySelectionUX(item);
          }}
          onKeyPress={() => {
            handleClick(item);
            applySelectionUX(item);
          }}
        >
          {isNone ? `${item.label}` : `${item.value} - ${item.label}`}
        </Option>
      );
    });

  const renderNoneOption = () => {
    if (!allowNone) return null;

    const noneItem: any = {
      value: -1,
      label: noneOptionLabel,
    };

    const isSelected = selected?.value === -1;

    return (
      <Option
        key="__none__"
        className={isSelected ? 'selected' : ''}
        onClick={() => {
          handleClick(noneItem);
          applySelectionUX(noneItem);
        }}
        onKeyPress={() => {
          handleClick(noneItem);
          applySelectionUX(noneItem);
        }}
      >
        {noneOptionLabel}
      </Option>
    );
  };

  const renderGrouped = () => {
    if (!flatItems.length) {
      return (
        <>
          {renderNoneOption()}
          {/* <Option className="not-found">Nenhum registro encontrado.</Option> */}
        </>
      );
    }

    if (!hasMultipleLojas) {
      return (
        <>
          {renderNoneOption()}
          {renderItems(flatItems as any[])}
        </>
      );
    }

    return (
      <>
        {renderNoneOption()}
        {uniqueLojas.map((lj) => {
          const itemsLoja = (flatItems as any[]).filter((i) => i.lojaId === lj);
          if (!itemsLoja.length) return null;

          return (
            <li
              key={`group-${lj}`}
              style={{ listStyle: 'none', width: '100%' }}
            >
              <div
                style={{ fontWeight: 600, padding: '4px 8px', opacity: 0.8 }}
              >
                {`Loja ${lj}`}
              </div>
              {renderItems(itemsLoja)}
            </li>
          );
        })}
      </>
    );
  };

  const mustShowEmpty =
    data &&
    (data.pages?.[0]?.message === 'url not found' ||
      data.pages?.[0]?.data?.length <= 0 ||
      (data.pages?.[0]?.message === 'Registros encontrados' &&
        data.pages?.[0]?.data?.length <= 0));

  return (
    <Content listaWidth={listaWidth}>
      <ul ref={wrapperRef} onScroll={onScroll}>
        {!data && !isFetching && (
          <Option className="not-found">
            Digite ao menos 3 caracteres para pesquisar.
          </Option>
        )}

        {data && renderGrouped()}

        {mustShowEmpty && !allowNone && (
          <Option className="not-found">Nenhum registro encontrado.</Option>
        )}

        {isFetching && (
          <Option className="not-found">
            <Spinner animation="border" size="sm" className="spinner" />
          </Option>
        )}
      </ul>
    </Content>
  );
};
